import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  CURRENT_USER_KEY
} from '@/lib/api';

export interface CurrentUser {
  id: number;
  email: string;
  name?: string;
  accountId?: number;
}

/**
 * Raw-string storage — stores the value verbatim (NO JSON encoding). Required
 * for the token atoms because `lib/api.ts` reads the tokens straight from
 * localStorage and puts them in the `Authorization: Bearer <token>` header.
 * The default atomWithStorage storage JSON-encodes, which would wrap the token
 * in quotes and produce a malformed JWT (→ 401 on every authed request).
 */
const rawStringStorage = {
  getItem: (key: string, initialValue: string | null): string | null => {
    return localStorage.getItem(key) ?? initialValue;
  },
  setItem: (key: string, value: string | null): void => {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  },
  removeItem: (key: string): void => localStorage.removeItem(key)
};

export const accessTokenAtom = atomWithStorage<string | null>(
  ACCESS_TOKEN_KEY,
  null,
  rawStringStorage
);
export const refreshTokenAtom = atomWithStorage<string | null>(
  REFRESH_TOKEN_KEY,
  null,
  rawStringStorage
);
export const currentUserAtom = atomWithStorage<CurrentUser | null>(
  CURRENT_USER_KEY,
  null
);

export const isAuthenticatedAtom = atom((get) => {
  const token = get(accessTokenAtom);
  return token !== null && token !== '';
});

export const loginAtom = atom(
  null,
  (
    _get,
    set,
    payload: { token: string; refreshToken: string; user: CurrentUser }
  ) => {
    set(accessTokenAtom, payload.token);
    set(refreshTokenAtom, payload.refreshToken);
    set(currentUserAtom, payload.user);
  }
);

export const logoutAtom = atom(null, (_get, set) => {
  set(accessTokenAtom, null);
  set(refreshTokenAtom, null);
  set(currentUserAtom, null);
});
